import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ProductStoreCategory,
  FetchProductStoreCategoriesRequest,
  FetchProductStoreCategoriesResponse,
} from "../api/fetchProductStoreCategories/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchProductStoreCategories } from "../api/fetchProductStoreCategories/fetchProductStoreCategoriesApi";
import { createProductStoreCategory } from "../api/createProductStoreCategory/createProductStoreCategoryApi";
import { updateProductStoreCategory } from "../api/updateProductStoreCategory/updateProductStoreCategoryApi";
import { deleteProductStoreCategory } from "../api/deleteProductStoreCategory/deleteProductStoreCategoryApi";
import { CreateProductStoreCategoryRequest } from "../api/createProductStoreCategory/types";

interface ProductStoreCategoriesState {
  categories: ProductStoreCategory[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: ProductStoreCategoriesState = {
  categories: [],
  totalCount: 0,
  loading: false,
  error: null,
};

const productStoreCategoriesSlice = createSlice({
  name: "productStoreCategories",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setCategoriesData: (
      state,
      action: PayloadAction<{
        categories: ProductStoreCategory[];
        meta: { totalCount: number };
      }>,
    ) => {
      state.categories = action.payload.categories;
      state.totalCount = action.payload.meta.totalCount;
    },
    addCategory: (state, action: PayloadAction<ProductStoreCategory>) => {
      state.categories.push(action.payload);
      state.totalCount += 1;
    },
    updateCategoryInState: (
      state,
      action: PayloadAction<ProductStoreCategory>,
    ) => {
      const index = state.categories.findIndex(
        (c) => c.id === action.payload.id,
      );
      if (index !== -1) {
        state.categories[index] = action.payload;
      }
    },
    removeCategory: (state, action: PayloadAction<string>) => {
      state.categories = state.categories.filter(
        (c) => c.id !== action.payload,
      );
      state.totalCount -= 1;
    },
    resetProductStoreCategories: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setCategoriesData,
  addCategory,
  updateCategoryInState,
  removeCategory,
  resetProductStoreCategories,
} = productStoreCategoriesSlice.actions;

export const fetchProductStoreCategoriesAction =
  (
    requestData: FetchProductStoreCategoriesRequest,
    signal?: AbortSignal,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: FetchProductStoreCategoriesResponse =
        await fetchProductStoreCategories(requestData, signal);

      const categories = response.data || [];
      const totalCount = response.meta?.totalCount || 0;

      dispatch(
        setCategoriesData({
          categories,
          meta: { totalCount },
        }),
      );
    } catch (error) {
      if (error instanceof Error && error.name === "CanceledError") {
        return;
      }
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch product store categories"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export const createProductStoreCategoryAction =
  (data: CreateProductStoreCategoryRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await createProductStoreCategory(data);
      dispatch(addCategory(response.data));
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to create product store category"));
      }
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updateProductStoreCategoryAction =
  (id: string, data: CreateProductStoreCategoryRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await updateProductStoreCategory(id, data);
      dispatch(updateCategoryInState(response.data));
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to update product store category"));
      }
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const deleteProductStoreCategoryAction =
  (id: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      await deleteProductStoreCategory(id);
      dispatch(removeCategory(id));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to delete product store category"));
      }
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export default productStoreCategoriesSlice.reducer;
